import { Navigate, Route, Routes, useNavigate } from 'react-router';
import { ProfileMenu } from './ProfileMenu';
import type { ProfileAppProps } from './ProfileApp';
import { BackButton, VerticalContainer } from '@/components';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { DeleteAccount } from '@/features/profile/widgets/delete-account/DeleteAccountApp';
import { Access } from '@/features/profile/widgets/access/Access';
import { CouponDetails } from '@/features/profile/widgets/access/ui/CouponDetails';
import { TrainingProgramDetails } from '@/features/profile/widgets/access/ui/TrainingProgramDetails';
import { SubscriptionDetails } from '@/features/profile/widgets/subscription-details/SubscriptionDetails';
import { Household } from '@/features/profile/widgets/household/HouseholdApp';
import { Newsletter } from '@/features/profile/widgets/newsletter/Newsletter';
import { Pets } from '@/features/profile/widgets/pets/PetsApp';
import { Language } from '@/features/profile/widgets/language/Language';
import { MyInformationApp } from '@/features/profile/widgets/my-information/MyInformationApp';
import { OnlinePharmacy } from '@/features/profile/widgets/online-pharmacy/OnlinePharmacy';
import { InsurancePartnerDetails } from '@/features/profile/widgets/insurance-partner-details/InsurancePartnerDetails';
import { SelectInsurancePartner } from '@/features/profile/widgets/select-insurance-partner/SelectInsurancePartner';
import { ProfilePaths } from '@/features/profile/model/types';
import { accessSubPaths } from '@/features/profile/model/profileMenuItems';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

export const ProfileRoutes = (props: ProfileAppProps) => {
    const { pathname } = useLocation();
    const previousPathname = useRef(pathname);
    const shouldFocusContent = previousPathname.current !== pathname;

    useEffect(() => {
        previousPathname.current = pathname;
        window.scrollTo(0, 0);
    }, [pathname]);

    const routes = [
        {
            path: '/',
            element: (
                <ProfileMenu
                    bootstrappedSelf={props.selfResponse}
                    isAdolescent={props.isAdolescent}
                    hasVeterinarianCoverage={props.hasVeterinarianCoverage}
                />
            ),
        },
        {
            path: ProfilePaths.MyInformation,
            element: <MyInformationApp bootstrappedSelf={props.selfResponse} />,
        },
        {
            path: ProfilePaths.Access,
            element: (
                <Access
                    companyAccessInfo={props.companyAccessInfo}
                    couponRedemptions={props.couponRedemptions}
                    trainingPrograms={props.trainingPrograms}
                    subscriptions={props.subscriptions}
                />
            ),
        },
        {
            path: ProfilePaths.CouponDetails,
            element: (
                <CouponDetails couponRedemptions={props.couponRedemptions} />
            ),
        },
        {
            path: ProfilePaths.TrainingProgramDetails,
            element: (
                <TrainingProgramDetails
                    trainingPrograms={props.trainingPrograms}
                />
            ),
        },
        {
            path: ProfilePaths.SubscriptionDetails,
            element: (
                <SubscriptionDetails subscriptions={props.subscriptions} />
            ),
        },
        {
            path: ProfilePaths.InsurancePartnerDetails,
            element: props.companyAccessInfo.Company?.CompanyName ? (
                <InsurancePartnerDetails
                    companyAccessInfo={props.companyAccessInfo}
                    benefits={props.insurancePartnerBenefits}
                    pfaProgressCards={props.pfaProgressCards}
                />
            ) : (
                <Navigate to={`/${ProfilePaths.Access}`} replace />
            ),
        },
        {
            path: ProfilePaths.SelectInsurancePartner,
            element: (
                <SelectInsurancePartner
                    providers={props.publicInsurancePartners}
                />
            ),
        },
        {
            path: ProfilePaths.Household,
            element: props.isAdolescent ? (
                <Navigate to={`/${ProfilePaths.MyInformation}`} replace />
            ) : (
                <Household
                    selfResponse={props.selfResponse}
                    canInvite={props.canInviteHouseholdMembers}
                    terms={props.adultHouseholdMemberTerms}
                    isAdolescentEnabled={props.isAdolescentEnabled}
                    adolescentMinimumAge={props.adolescentMinimumAge}
                    adultMinimumAge={props.adultMinimumAge}
                />
            ),
        },
        {
            path: ProfilePaths.Pets,
            element: props.hasVeterinarianCoverage ? (
                <Pets bootstrappedPets={props.pets} />
            ) : (
                <Navigate to={`/${ProfilePaths.MyInformation}`} replace />
            ),
        },
        {
            path: ProfilePaths.Language,
            element: (
                <Language
                    supportedLanguages={props.supportedLanguages}
                    currentLanguage={props.currentLanguage}
                />
            ),
        },
        {
            path: ProfilePaths.Newsletter,
            element: (
                <Newsletter
                    isMarketingAccepted={props.isMarketingAccepted}
                    email={props.email}
                    isTopdanmark={props.isTopdanmark}
                />
            ),
        },
        {
            path: ProfilePaths.OnlinePharmacy,
            element: <OnlinePharmacy />,
        },
        {
            path: ProfilePaths.DeleteAccount,
            element: (
                <DeleteAccount
                    showSubscriptionWarning={props.showSubscriptionWarning}
                    showAdolescentWarning={props.showAdolescentWarning}
                    adolescentMinimumAge={props.adolescentMinimumAge}
                    adultMinimumAge={props.adultMinimumAge}
                />
            ),
        },
    ];

    return (
        <Routes>
            {routes.map(({ path, element }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProfileSection
                            path={path}
                            element={element}
                            shouldFocus={shouldFocusContent}
                        />
                    }
                />
            ))}

            <Route
                path="*"
                element={
                    <Navigate to={`/${ProfilePaths.MyInformation}`} replace />
                }
            />
        </Routes>
    );
};

const ProfileSection = ({
    path,
    element,
    shouldFocus,
}: {
    path: string;
    element: React.ReactNode;
    shouldFocus: boolean;
}) => {
    const navigate = useNavigate();
    const isAccessSubPage = accessSubPaths.some(
        (p) => p.toLowerCase() === path.toLowerCase(),
    );
    const isMenuRoute = path === '/';
    const headingPath = isMenuRoute ? ProfilePaths.MyInformation : path;
    const sectionId = `profile-section-${headingPath}`;
    const backButtonOnClick = isAccessSubPage
        ? () => navigate(`/${ProfilePaths.Access}`)
        : () => navigate('/');

    useEffect(() => {
        if (shouldFocus) {
            document.getElementById(sectionId)?.focus();
        }
    }, [sectionId, shouldFocus]);

    return (
        <VerticalContainer
            spacing="md"
            as="section"
            role="region"
            id={sectionId}
            aria-label={isMenuRoute ? R.MyProfile : undefined}
            aria-labelledby={isMenuRoute ? undefined : `heading-${headingPath}`}
            tabIndex={-1}
            className="h-full focus-visible:ring-2 focus-visible:ring-(--color-border-focus-primary) focus-visible:ring-offset-4 focus-visible:outline-none"
        >
            {!isMenuRoute && (
                <BackButton className="md:hidden" onClick={backButtonOnClick} />
            )}
            {element}
        </VerticalContainer>
    );
};
